import React, { useContext } from 'react'
import { CircularLoading, Icon, SIZE_EXTRA_SMALL, Tooltip, TYPE_OUTLINE } from '@mds/mds-reactjs-library'
import Outline16Check from '@mds/mds-icons/icons/svg/outline-16-check.svg'
import Outline16Database from '@mds/mds-icons/icons/svg/outline-16-database.svg'
import ReportingEndpoints from '../../Services/ReportingEndpoints'
import { useCreateToast } from '../../Hooks/useCreateToast'
import { ChargeCodeContext } from '../../Contexts/ChargeCode/ChargeCodeContext'
import { isChargeCodeRequired } from '../../Utility/ChargeCodeUtility'
import { buildChargeCodeDetailsJson } from '../../Utility/ChargeCodeDetailsPayload'

const LibraryTableDownloadsColumn = ({summaryEntry, addReportRequest, generators}) => {
  const { createErrorToast } = useCreateToast()
  const { chargeCode } = useContext(ChargeCodeContext)

  const downloadReport = (reportRequestId, isData) => {
    ReportingEndpoints
      .downloadReport(reportRequestId, isData)
      .then((res) => {
        let alink = document.createElement('a');
        const dataUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/octet-stream' }));
        alink.href = dataUrl;
        alink.download = res.headers['content-disposition'].split('filename=')[1].split(';')[0];
        alink.click();
      })
      .catch(e => createErrorToast(e));
  }

const generateReport = (summaryEntry, generator, chargeCodeValue, reportExtra = {}) => {
    const code = chargeCodeValue ?? chargeCode?.value ?? '6393YN01'
    const payload = {
      generatedDataId : summaryEntry.generatedDataId,
      reportGeneratorId : generator.reportGeneratorId,
      datasetId : summaryEntry.datasetId,
      chargeCode: code
    }
    if (reportExtra?.details) {
      payload.details = reportExtra.details
    } else if (isChargeCodeRequired() && chargeCode?.value) {
      payload.details = buildChargeCodeDetailsJson({
        chargeCodeValue: chargeCode.value,
        eDEmail: chargeCode.ed_email || '',
        isIAmEDChecked: chargeCode.isIAmEDChecked,
        isAgreementPolicyChecked: chargeCode.isAgreementPolicyChecked,
        isWarRoomChecked: chargeCode.isWarRoomChecked
      })
    }
    ReportingEndpoints.createReportRequestWithGeneratedData(payload)
    .then(({data}) => {
      let reportRequest = {        
        reportRequestId: data.reportrequestid,
        name: data.name,        
        datasetId: data.datasetid,
        publicationId: summaryEntry.requests[0].publicationId,
        publicationCreatedDate: summaryEntry.requests[0].publicationCreatedDate,
        datasetName: summaryEntry.datasetName,
        displayName: summaryEntry.requests[0].displayName,
        reportStatusName: "Report Queued",        
        reportTypeName: summaryEntry.reportTypeName,
        sourceName: summaryEntry.sourceName,
        createdDate: data.createddate,
        generatedDataId: data.generateddataid,
        generatedReportId: data.generatedreportid,
        reportFormatName: data.generatedreport.reportgenerator.reportformat.name,
        reportFileName: null,
        reportGeneratorName: data.generatedreport.reportgenerator.name,
        checkChargeCode: false
      }
      addReportRequest(reportRequest);
    })
    .catch(e => createErrorToast(e));
  }

  const isDataGeneratingInProgress = (summaryEntry) => {
    return summaryEntry && ["data queued", "generating data"].includes(summaryEntry.reportStatusName.toLowerCase());
  }

  const isDataGenerated = (summaryEntry) => {
    return summaryEntry && ["data generated", "report queued", "generating report", "available", "failed"].includes(summaryEntry.reportStatusName.toLowerCase());
  }

  const isDataGenerationFailed = (summaryEntry) => {
    return summaryEntry && summaryEntry.requests[0].reportStatusName.toLowerCase() === "data generation failed";
  }

  const isReportGeneratingInProgress = (request) => {
    return request && ["report queued", "generating report"].includes(request.reportStatusName.toLowerCase());
  }

  const getRawDataTooltip = (summaryEntry) => {
    if(isDataGenerated(summaryEntry)) 
      return 'Download';
    if(isDataGeneratingInProgress(summaryEntry))
      return 'In progress';
    if(isDataGenerationFailed(summaryEntry))
      return 'Generation failed';
    return 'Queued';
  }

  const getGeneratorTooltip = (request) => {
    if(request.reportStatusName.toLowerCase() === 'available')
      return 'Download';
    if(isReportGeneratingInProgress(request))
      return 'In progress';
    if(request.reportStatusName.toLowerCase() === 'failed')
      return 'Generation failed';
    return 'Create export';
  }

  return (
    <div className="buttons">
      {
        summaryEntry && summaryEntry.requests.length > 0 && 
          <>
            {/* <Tooltip dark={false} content={getRawDataTooltip(summaryEntry)}> */}
              <span className={`raw-data-status ${isDataGeneratingInProgress(summaryEntry) ? 'progress' : isDataGenerated(summaryEntry) ? 'complete' : isDataGenerationFailed(summaryEntry) ? 'failed' : ''}`}>
                {isDataGeneratingInProgress(summaryEntry) ? (
                  <CircularLoading indeterminate size={SIZE_EXTRA_SMALL} />
                ) : isDataGenerated(summaryEntry) ? (
                  <Icon type={TYPE_OUTLINE} src={Outline16Check} />
                ) : (
                  <Icon type={TYPE_OUTLINE} name="down-outline-16" src={Outline16Database} />
                )}
              </span>
            {/* </Tooltip> */}
            {
              isDataGenerated(summaryEntry) &&
              generators.map(generator => { 
                  let request = summaryEntry.requests.find(request => request.reportGeneratorName === generator.name);
                  if (request)
                    return (
                    <Tooltip key={generator.reportGeneratorId} dark={false} content={ getGeneratorTooltip(request) }>
                      <button className={`xls ${request.reportStatusName.toLowerCase() === 'available' ? 'active' : request.reportStatusName.toLowerCase() === 'failed' ? 'failed' : ''}`} onClick={() => 
                        {
                          if(request.reportStatusName.toLowerCase() !== 'available')
                            return;
                          downloadReport(request.reportRequestId, false);
                        }}>
                        <img src={`data:${generator.thumbnailMimeType};base64,${generator.thumbnail}`} alt={generator.name} />
                        {isReportGeneratingInProgress(request) && <div className='progress'></div>}
                        <span>{generator.displayName}</span>
                      </button>
                    </Tooltip>)
                  else 
                    return (
                    <Tooltip key={generator.reportGeneratorId} dark={false} content='Create export'>
                      <button
                        className="xls"
                        onClick={() => {
                          generateReport(summaryEntry, generator)
                        }}
                      >
                        <img src={`data:${generator.thumbnailMimeType};base64,${generator.thumbnail}`} alt={generator.name} />
                        {generator.displayName}
                      </button>
                    </Tooltip>)
                }
              )
            }
          </>
      }
    </div>
  )
}

export default LibraryTableDownloadsColumn
